import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiUrl } from '../utils/api'
import {
  ArrowLeft,
  CheckCircle2,
  Trophy,
  RotateCcw,
  ChevronRight,
  HelpCircle,
  AlertCircle,
} from 'lucide-react'
import '../styles/course.css'

interface QuizOption {
  id: string
  text: string
}

interface QuizQuestion {
  id: string
  text: string
  options: QuizOption[]
}

interface Quiz {
  questions: QuizQuestion[]
}

interface QuestionResult {
  questionId: string
  correctOptionId: string | null
  selectedOptionId: string | null
  correct: boolean
}

interface QuizResult {
  score: number
  total: number
  percentage: number
  passed: boolean
  bestScore: number
  bestPercentage: number
  results: QuestionResult[]
}

export default function QuizPage() {
  const { courseId, moduleId } = useParams()
  const { token } = useAuth()
  const navigate = useNavigate()

  const [moduleTitle, setModuleTitle] = useState('')
  const [quiz, setQuiz] = useState<Quiz | null>(null)
  const [bestPercentage, setBestPercentage] = useState<number | null>(null)
  const [quizAttempted, setQuizAttempted] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setError('')
      try {
        const res = await fetch(apiUrl(`/api/courses/${courseId}/modules/${moduleId}`), {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Failed to load quiz')
        if (!data.hasQuiz) throw new Error('This lesson has no quiz')

        setModuleTitle(data.module?.headline || 'Lesson')
        setQuiz(data.quiz || null)
        setQuizAttempted(!!data.quizAttempted)
        setBestPercentage(data.quizAttempted ? data.quizBestPercentage ?? null : null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load quiz')
      } finally {
        setIsLoading(false)
      }
    }
    load()
    window.scrollTo(0, 0)
  }, [courseId, moduleId, token])

  const allAnswered = quiz ? quiz.questions.every((q) => answers[q.id]) : false

  const submitQuiz = async () => {
    if (!quiz || !allAnswered) return
    setSaving(true)
    setError('')
    try {
      const res = await fetch(apiUrl(`/api/courses/${courseId}/modules/${moduleId}/quiz`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to submit quiz')
      setQuizResult({
        score: data.score,
        total: data.total,
        percentage: data.percentage,
        passed: data.passed,
        bestScore: data.bestScore,
        bestPercentage: data.bestPercentage,
        results: data.results || [],
      })
      setBestPercentage(data.bestPercentage)
      setQuizAttempted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit quiz')
    } finally {
      setSaving(false)
    }
  }

  const retryQuiz = () => {
    setQuizResult(null)
    setAnswers({})
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (isLoading) return <div className="loading">Loading quiz…</div>

  if (error && !quiz) {
    return (
      <div className="quiz-page-container">
        <div className="quiz-page-topbar">
          <button className="back-link-btn" onClick={() => navigate(`/course/${courseId}/module/${moduleId}`)}>
            <ArrowLeft size={15} /> Back to Lesson
          </button>
        </div>
        <div className="error-message" style={{ maxWidth: 720, margin: '40px auto', padding: '0 24px' }}>
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="quiz-page-container">
      {/* Topbar */}
      <div className="quiz-page-topbar">
        <button
          className="back-link-btn"
          onClick={() => navigate(`/course/${courseId}/module/${moduleId}`)}
        >
          <ArrowLeft size={15} /> Back to Lesson
        </button>
        {quizAttempted && bestPercentage !== null && (
          <span className={`quiz-best-pill ${bestPercentage >= 75 ? 'passed' : 'failed'}`}>
            {bestPercentage >= 75
              ? <><CheckCircle2 size={13} /> Passed — best {bestPercentage}%</>
              : <><AlertCircle size={13} /> Best {bestPercentage}%</>}
          </span>
        )}
      </div>

      {/* Result banner (shown after submission, above questions) */}
      {quizResult && (
        <div className={`quiz-result-banner ${quizResult.passed ? 'passed' : 'failed'}`}>
          <div className="quiz-result-banner-icon">
            {quizResult.passed ? <Trophy size={28} /> : <RotateCcw size={28} />}
          </div>
          <div className="quiz-result-banner-body">
            <h2>{quizResult.passed ? 'Quiz Passed!' : 'Not quite — try again'}</h2>
            <p>
              You scored <strong>{quizResult.score}/{quizResult.total}</strong> ({quizResult.percentage}%)
              {quizResult.bestPercentage !== quizResult.percentage &&
                ` · Best: ${quizResult.bestPercentage}%`}
              {quizResult.passed
                ? ' — above the 75% pass mark.'
                : ' — you need 75% to pass this lesson.'}
            </p>
          </div>
          <div className="quiz-result-banner-actions">
            <button className="nav-btn" onClick={retryQuiz}>
              <RotateCcw size={14} /> Retry
            </button>
            <button
              className="complete-btn"
              onClick={() => navigate(`/course/${courseId}`)}
            >
              Back to Course <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Quiz card - hidden after submission */}
      {!quizResult && (
        <div className="quiz-standalone-card">
          <div className="quiz-standalone-header">
            <div className="quiz-standalone-meta">
              <HelpCircle size={18} className="quiz-meta-icon" />
              <span className="quiz-meta-label">Checkpoint Quiz</span>
            </div>
            <h1 className="quiz-standalone-title">{moduleTitle}</h1>
            <p className="quiz-standalone-desc">
              {quiz?.questions.length} question{quiz?.questions.length !== 1 ? 's' : ''} · pass mark 75%
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <ol className="quiz-questions">
            {quiz?.questions.map((q, qi) => {
              return (
                <li key={q.id} className="quiz-question">
                  <p className="quiz-q-text">
                    <span className="quiz-q-num">{qi + 1}.</span> {q.text}
                  </p>
                  <div className="quiz-options">
                    {q.options.map((o) => {
                      const selected = answers[q.id] === o.id
                      let optClass = 'quiz-option'
                      if (selected) optClass += ' selected'
                      return (
                        <label key={o.id} className={optClass}>
                          <input
                            type="radio"
                            name={`q-${q.id}`}
                            value={o.id}
                            checked={selected}
                            disabled={!!quizResult}
                            onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: o.id }))}
                          />
                          <span className="quiz-option-indicator" />
                          <span>{o.text}</span>
                        </label>
                      )
                    })}
                  </div>
                </li>
              )
            })}
          </ol>

          <button
            className="complete-btn quiz-submit-btn"
            onClick={submitQuiz}
            disabled={!allAnswered || saving}
          >
            {saving ? 'Submitting…' : <>Submit Answers <ChevronRight size={15} /></>}
          </button>
        </div>
      )}
    </div>
  )
}
